import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import {
  readSnapshotBlocks,
  type PreviewBlock,
  type VersionSnapshotShape,
} from "./versionPreview";
import { Textarea } from "@/components/lilia-ui/textarea";
import { Spinner } from "@/components/lilia-ui/spinner";
import {
  IconDeviceFloppy,
  IconArrowBackUp,
  IconClock,
  IconEyeOff,
  IconGitBranch,
  IconHistory,
} from "@tabler/icons-react";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import type { DocumentVersion } from "@/lib/api";
import {
  emptyBecauseFiltered,
  hiddenCount,
  visibleVersions,
} from "./versionTimeline";
import { cn } from "@/lib/cn";
import { useTranslation } from "@/utils/hooks/useTranslation";

interface VersionHistoryPanelProps {
  documentId: string;
  onVersionRestored?: () => void;
  onVersionCreated?: () => void;
}

const SHOW_AUTOMATIC_KEY = "lilia-history-show-automatic";

export function VersionHistoryPanel({
  documentId,
  onVersionRestored,
  onVersionCreated,
}: VersionHistoryPanelProps) {
  const { t } = useTranslation('panels');
  const { fetchWithAuth } = useApiContext();
  const navigate = useNavigate();
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create version dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [versionComment, setVersionComment] = useState("");
  const [creating, setCreating] = useState(false);

  // Restore confirmation dialog
  const [restoreVersion, setRestoreVersion] = useState<DocumentVersion | null>(null);
  // Branch confirmation — separate from restore because they are opposite
  // actions: restore moves this document, branching leaves it alone.
  const [branchVersion, setBranchVersion] = useState<DocumentVersion | null>(null);
  const [branching, setBranching] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<DocumentVersion | null>(null);
  const [previewBlocks, setPreviewBlocks] = useState<PreviewBlock[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  /**
   * The version the document is currently on.
   *
   * The server answers this now (lilia-editor-api #115): restoring moves a
   * marker back rather than appending, and the marker is validated against the
   * document's real content, so it clears itself the moment anyone types.
   *
   * That replaces two earlier guesses, both of which were wrong in the same
   * way. `index === 0` asked "which is first in the list". Deriving the highest
   * version number asked "which was saved last". Neither is the question — the
   * document can be sitting on v1 with v7 saved, and only the server knows.
   *
   * Null is a real answer and means "not on any saved version", which is the
   * normal state while editing. Nothing is highlighted then, which is honest.
   */
  const currentVersionId = useMemo(
    () => versions.find((v) => v.isCurrent)?.id ?? null,
    [versions],
  );

  /**
   * Whether the machine's own rows are on show.
   *
   * Off by default. Auto-saves and the rescue row a restore writes are both
   * flagged isAutoSave, and showing them beside the checkpoints an author made
   * is why restoring read as making the history longer.
   */
  const [showAutomatic, setShowAutomaticRaw] = useState<boolean>(() => {
    try { return localStorage.getItem(SHOW_AUTOMATIC_KEY) === "true"; }
    catch { return false; }
  });
  const setShowAutomatic = useCallback((next: boolean) => {
    setShowAutomaticRaw(next);
    try { localStorage.setItem(SHOW_AUTOMATIC_KEY, String(next)); } catch { /* ignore */ }
  }, []);

  const shown = useMemo(
    () => visibleVersions(versions, showAutomatic),
    [versions, showAutomatic],
  );
  const hidden = hiddenCount(versions, showAutomatic);

  const [restoring, setRestoring] = useState(false);

  // Fetch versions
  const fetchVersions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/versions?limit=50`
      );
      const versionList = await handleResponse<DocumentVersion[]>(response);
      // Sort here rather than trusting arrival order. "Which one am I
        // looking at" is answered by version number below, and that answer
        // must not depend on how the API happened to serialise the list.
        setVersions([...versionList].sort((a, b) => b.versionNumber - a.versionNumber));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('history.failedToLoadVersions'));
    } finally {
      setLoading(false);
    }
  }, [documentId, fetchWithAuth]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  // Create version
  const handleCreateVersion = async () => {
    try {
      setCreating(true);
      await fetchWithAuth(`${API_BASE}/documents/${documentId}/versions`, {
        method: "POST",
        body: JSON.stringify({ name: versionComment || undefined }),
      });
      setShowCreateDialog(false);
      setVersionComment("");
      await fetchVersions();
      onVersionCreated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('history.failedToCreateVersion'));
    } finally {
      setCreating(false);
    }
  };

  /**
   * Read a version's snapshot so it can be looked at before it is restored.
   *
   * Restoring is safe now — the current state is preserved first — but "safe to
   * undo" is not the same as "worth doing", and the list only shows a number, a
   * date and a name. Being able to see the text is what makes choosing between
   * two versions possible at all.
   */
  const openPreview = useCallback(async (version: DocumentVersion) => {
    setPreviewVersion(version);
    setPreviewBlocks(null);
    setPreviewError(null);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/versions/${version.id}`,
      );
      const full = await handleResponse<{ snapshot?: VersionSnapshotShape }>(response);
      setPreviewBlocks(readSnapshotBlocks(full?.snapshot));
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : t('history.failedToLoadVersions'));
    }
  }, [documentId, fetchWithAuth, t]);

  // Restore version
  const handleRestoreVersion = async () => {
    if (!restoreVersion) return;

    try {
      setRestoring(true);
      await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/versions/${restoreVersion.id}/restore`,
        { method: "POST" }
      );
      setRestoreVersion(null);
      await fetchVersions();
      onVersionRestored?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('history.failedToRestoreVersion'));
    } finally {
      setRestoring(false);
    }
  };

  /**
   * Copy a version into a new document and open it.
   *
   * This document is untouched — no marker moves, nothing is written to it.
   * That is the difference from restore, and it is why this navigates away:
   * the branch is a different document, so staying here would leave you looking
   * at the one you did not just create.
   */
  const handleBranchVersion = async () => {
    if (!branchVersion) return;

    try {
      setBranching(true);
      const created = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/versions/${branchVersion.id}/branch`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        },
      ).then((r) => handleResponse<{ id: string }>(r));

      setBranchVersion(null);
      navigate(`/d/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('history.failedToBranch'));
    } finally {
      setBranching(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('history.justNow');
    if (diffMins < 60) return t('history.minAgo', { count: diffMins });
    if (diffHours < 24) return t('history.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('history.daysAgo', { count: diffDays });

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  // Format size
  const formatSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center px-4 py-3 border-b border-[var(--lilia-border)]">
        <h3 className="m-0 text-sm font-semibold flex items-center gap-2 text-foreground">
          <IconClock size={16} />
          {t('history.title')}
        </h3>
        <Button
          data-testid="version-create-trigger"
          variant="subtle"
          size="sm"
          onClick={() => setShowCreateDialog(true)}
          title={t('history.createVersion')}
        >
          <IconDeviceFloppy size={16} className="mr-1.5" />
          {t('history.saveVersion')}
        </Button>
      </div>

      {/* The switch, and only when it would reveal something. A control that
          does nothing is worse than no control — and on a document with no
          automatic saves there is nothing behind it. */}
      {(hidden > 0 || showAutomatic) && (
        <div className="flex items-center justify-end px-4 pb-2">
          <button
            type="button"
            data-testid="history-automatic-toggle"
            data-showing={showAutomatic ? "true" : "false"}
            onClick={() => setShowAutomatic(!showAutomatic)}
            title={showAutomatic ? t('history.hideAutomatic') : t('history.showAutomatic')}
            className="inline-flex items-center gap-1.5 text-[11.5px] text-[var(--lilia-text-muted,#64748b)] hover:text-[var(--lilia-text,#0f172a)]"
          >
            {showAutomatic ? <IconEyeOff size={13} /> : <IconHistory size={13} />}
            {showAutomatic
              ? t('history.hideAutomatic')
              : t('history.automaticCount', { count: hidden })}
          </button>
        </div>
      )}

      {error && <div className="px-4 py-3 bg-red-50 text-red-600 text-[13px]">{error}</div>}

      {loading ? (
        <div className="flex justify-center items-center p-10">
          <Spinner size="md" />
        </div>
      ) : emptyBecauseFiltered(versions, showAutomatic) ? (
        // Not "no versions yet" — there are some, they are all automatic.
        // Saying otherwise while several sit behind the switch would be a lie.
        <div className="py-6 px-4 text-center text-muted-foreground">
          <p className="m-0 mb-2">{t('history.noCheckpointsYet')}</p>
          <p className="text-xs text-muted-foreground">
            {t('history.automaticOnlyHint', { count: versions.length })}
          </p>
        </div>
      ) : versions.length === 0 ? (
        <div className="py-6 px-4 text-center text-muted-foreground">
          <p className="m-0 mb-2">{t('history.noVersionsSavedYet')}</p>
          <p className="text-xs text-muted-foreground">
            {t('history.clickSaveVersionHint')}
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-2">
          {shown.map((version, index) => (
            <div
              key={version.id}
              data-testid="version-row"
              data-version-id={version.id}
              data-version-number={version.versionNumber}
              data-current={version.id === currentVersionId ? "true" : "false"}
              className={cn(
                "p-3 mb-2 rounded-lg border border-[var(--lilia-border)] bg-muted transition-colors hover:border-primary",
                version.id === currentVersionId &&
                  "border-l-[3px] border-l-primary bg-[var(--lilia-primary-subtle)]"
              )}
            >
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-[13px] text-foreground">
                  {t('history.versionNumber', { number: version.versionNumber })}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDate(version.createdAt)}
                </span>
              </div>

              {/* name, then comment. The API sends `name` — it always has — and
                  this rendered only `comment`, which nothing populates. So no
                  version name has ever appeared here: not the one you type when
                  saving, not "Auto-save", not "Unsaved work before v1". Telling
                  the rows apart is the entire point of the panel. */}
              {(version.name || version.comment) && (
                <div
                  data-testid="version-name"
                  className="text-[13px] text-foreground/80 mb-2 italic"
                >
                  {version.name || version.comment}
                </div>
              )}

              <div className="flex gap-3 text-[11px] text-muted-foreground mb-2">
                {version.creator && (
                  <span className="flex items-center gap-1">{version.creator.name}</span>
                )}
                {version.sizeBytes && (
                  <span className="flex items-center gap-1">{formatSize(version.sizeBytes)}</span>
                )}
              </div>

              <div className="flex gap-2 pt-2 border-t border-dashed border-[var(--lilia-border)]">
                <Button
                  data-testid="version-restore-trigger"
                  data-version-id={version.id}
                  variant="subtle"
                  size="sm"
                  onClick={() => setRestoreVersion(version)}
                  title={
                    version.id === currentVersionId
                      ? t('history.thisIsCurrentVersion')
                      : t('history.restoreThisVersion')
                  }
                >
                  <IconArrowBackUp size={16} className="mr-1.5" />
                  {t('history.restore')}
                </Button>
                <Button
                  data-testid="version-preview-trigger"
                  data-version-id={version.id}
                  variant="subtle"
                  size="sm"
                  onClick={() => void openPreview(version)}
                >
                  {t('history.preview')}
                </Button>
                <Button
                  data-testid="version-branch-trigger"
                  data-version-id={version.id}
                  variant="subtle"
                  size="sm"
                  onClick={() => setBranchVersion(version)}
                  title={t('history.branchThisVersion')}
                >
                  <IconGitBranch size={16} className="mr-1.5" />
                  {t('history.branch')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview — read a version before deciding to restore it. */}
      <Dialog
        open={previewVersion !== null}
        onOpenChange={() => {
          setPreviewVersion(null);
          setPreviewBlocks(null);
          setPreviewError(null);
        }}
      >
        <DialogContent className="max-w-[560px]" data-testid="version-preview-dialog">
          <DialogHeader>
            <DialogTitle>
              {previewVersion
                ? t('history.versionNumber', { number: previewVersion.versionNumber })
                : ""}
            </DialogTitle>
          </DialogHeader>
          {previewError ? (
            <p className="text-destructive text-sm">{previewError}</p>
          ) : previewBlocks === null ? (
            <div className="flex justify-center py-8"><Spinner size="md" /></div>
          ) : previewBlocks.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="version-preview-empty">
              {t('history.previewEmpty')}
            </p>
          ) : (
            <div
              className="max-h-[46vh] overflow-y-auto rounded border border-[var(--lilia-border)] p-3"
              data-testid="version-preview-body"
            >
              {previewBlocks.map((b) => (
                <div key={b.id} data-testid="version-preview-block" data-block-type={b.type} className="mb-2">
                  <span className="mr-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {b.type}
                  </span>
                  <span className="text-[13px] text-foreground">{b.text}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            {previewVersion && (
              <Button
                data-testid="version-preview-branch"
                variant="subtle"
                onClick={() => {
                  const v = previewVersion;
                  setPreviewVersion(null);
                  setBranchVersion(v);
                }}
              >
                <IconGitBranch size={16} className="mr-1.5" />
                {t('history.branch')}
              </Button>
            )}
            {previewVersion && (
              <Button
                data-testid="version-preview-restore"
                onClick={() => {
                  const v = previewVersion;
                  setPreviewVersion(null);
                  setRestoreVersion(v);
                }}
              >
                {t('history.restore')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Version Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-[400px]" data-testid="version-create-dialog">
          <DialogHeader>
            <DialogTitle>{t('history.saveVersion')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-3 text-foreground">{t('history.createSnapshotDescription')}</p>
            <div className="mb-4">
              <label className="block mb-1 text-[13px] font-medium text-foreground">{t('history.commentOptional')}</label>
              <Textarea
                data-testid="version-create-comment"
                value={versionComment}
                onChange={(e) => setVersionComment(e.currentTarget.value)}
                placeholder="e.g., Added results section"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="version-create-cancel" onClick={() => setShowCreateDialog(false)}>{t('common:cancel')}</Button>
            <Button
              data-testid="version-create-confirm"
              variant="primary"
              onClick={handleCreateVersion}
              disabled={creating}
            >
              {creating ? t('history.saving') : t('history.saveVersion')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Branch — the other half of restore. Confirmed rather than immediate
          because it navigates: you end up in a different document, which is
          the point, and should not be a surprise. */}
      <Dialog open={branchVersion !== null} onOpenChange={() => setBranchVersion(null)}>
        <DialogContent data-testid="version-branch-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('history.branchVersionTitle', { number: branchVersion?.versionNumber ?? 0 })}
            </DialogTitle>
          </DialogHeader>
          {branchVersion && (
            <div className="py-2">
              <p className="text-[13px] text-foreground">
                {t('history.branchConfirmText', { number: branchVersion.versionNumber })}
              </p>
              <p className="mt-2 text-[12px] text-muted-foreground">
                {t('history.branchNote')}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="subtle" size="sm" onClick={() => setBranchVersion(null)}>
              {t('common:cancel')}
            </Button>
            <Button
              data-testid="version-branch-confirm"
              size="sm"
              disabled={branching}
              onClick={() => void handleBranchVersion()}
            >
              {branching ? t('history.branching') : t('history.createDocument')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreVersion !== null} onOpenChange={() => setRestoreVersion(null)}>
        <DialogContent className="max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('history.restoreVersion')}</DialogTitle>
          </DialogHeader>
          {restoreVersion && (
            <div className="py-4">
              <p className="mb-3 text-foreground">
                {t('history.restoreConfirmation', { version: restoreVersion.versionNumber })}
              </p>
              {restoreVersion.comment && (
                <p className="p-3 bg-muted rounded italic text-muted-foreground">"{restoreVersion.comment}"</p>
              )}
              <p className="text-[13px] text-muted-foreground p-3 bg-yellow-50 rounded mt-2">
                {t('history.restoreWarning')}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="version-restore-cancel" onClick={() => setRestoreVersion(null)}>{t('common:cancel')}</Button>
            <Button
              data-testid="version-restore-confirm"
              variant="primary"
              onClick={handleRestoreVersion}
              disabled={restoring}
            >
              {restoring ? t('history.restoring') : t('history.restore')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
